import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { notifyError, notifySuccess } from '../../utils/notifications';
// type="email" alone accepts addresses without a domain suffix (e.g. user@host) or with consecutive dots (e.g. a..b@example.com)
const emailAddressPattern = /(?:[a-z0-9!#$%&'*+\x2f=?^_`\x7b-\x7d~\x2d]+(?:\.[a-z0-9!#$%&'*+\x2f=?^_`\x7b-\x7d~\x2d]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9\x2d]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9\x2d]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9\x2d]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.source;
const isNonEmptyTrimmed = (value) => {
    return value.trim().length > 0;
};
const getInitialFormState = (options) => {
    return {
        username: options?.username ?? '',
        fullName: options?.fullName ?? '',
        emailAddress: options?.emailAddress ?? '',
        companyName: options?.companyName ?? '',
        telephoneNo: options?.telephoneNo ?? '',
    };
};
const isValid = (form, isEmailValid) => {
    return isNonEmptyTrimmed(form.fullName)
        && isEmailValid
        && isNonEmptyTrimmed(form.companyName)
        && isNonEmptyTrimmed(form.telephoneNo);
};
const isFieldValid = (fieldName, form, isEmailValid) => {
    switch (fieldName) {
        case 'fullName':
            return isNonEmptyTrimmed(form.fullName);
        case 'emailAddress':
            return isEmailValid;
        case 'companyName':
            return isNonEmptyTrimmed(form.companyName);
        case 'telephoneNo':
            return isNonEmptyTrimmed(form.telephoneNo);
    }
};
const getSubscriptionAlert = (daysLeft) => {
    if (daysLeft > 30) {
        return {
            className: 'alert alert-success',
            iconClass: 'fa fa-check',
            text: `Days left of subscription: ${daysLeft}`,
        };
    }
    if (daysLeft > 7) {
        return {
            className: 'alert alert-warning',
            iconClass: 'fa fa-warning',
            text: `Days left of subscription: ${daysLeft}`,
        };
    }
    if (daysLeft >= 0) {
        return {
            className: 'alert alert-danger',
            iconClass: 'fa fa-warning',
            text: `Days left of subscription: ${daysLeft}`,
        };
    }
    return {
        className: 'alert alert-danger',
        iconClass: 'fa fa-warning',
        text: 'Your subscription has expired.',
    };
};
export const AccountDetails = (props) => {
    const [form, setForm] = useState(() => getInitialFormState(props.options));
    const [showValidation, setShowValidation] = useState(false);
    const [touchedFields, setTouchedFields] = useState({
        fullName: false,
        emailAddress: false,
        companyName: false,
        telephoneNo: false,
    });
    const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const emailInputRef = useRef(null);
    // Let the browser validate the real input (type, required, pattern) once the new value is in the DOM
    useEffect(() => {
        setIsEmailValid(emailInputRef.current?.checkValidity() ?? false);
    }, [form.emailAddress]);
    useEffect(() => {
        setForm(getInitialFormState(props.options));
        setTouchedFields({
            fullName: false,
            emailAddress: false,
            companyName: false,
            telephoneNo: false,
        });
        setShowValidation(false);
    }, [props.options]);
    useEffect(() => {
        const loadSubscriptionDaysLeft = async () => {
            try {
                const response = await fetch('/api/settings/licenseenddate');
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                const daysLeft = await response.json();
                setSubscriptionDaysLeft(daysLeft);
            }
            catch {
                notifyError('An error has occurred when retrieving your subscription details.');
            }
        };
        void loadSubscriptionDaysLeft();
    }, []);
    const handleChange = (fieldName, value) => {
        setForm((current) => ({
            ...current,
            [fieldName]: value,
        }));
    };
    const handleBlur = (fieldName) => {
        setTouchedFields((current) => ({
            ...current,
            [fieldName]: true,
        }));
    };
    const saveAccountDetails = async () => {
        if (!isValid(form, isEmailValid)) {
            setShowValidation(true);
            return;
        }
        setIsSaving(true);
        try {
            const response = await fetch('/api/settings/updateaccount', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: form.username,
                    emailAddress: form.emailAddress,
                    fullName: form.fullName,
                    companyName: form.companyName,
                    telephoneNo: form.telephoneNo,
                    comments: '',
                    htmlEmails: true,
                }),
            });
            if (!response.ok) {
                const message = await response.text();
                notifyError(message || 'An error has occurred when trying to update your account details.');
                return;
            }
            setShowValidation(false);
            notifySuccess('Account details successfully updated');
        }
        catch {
            notifyError('An error has occurred when trying to update your account details.');
        }
        finally {
            setIsSaving(false);
        }
    };
    const subscriptionAlert = subscriptionDaysLeft == null ? null : getSubscriptionAlert(subscriptionDaysLeft);
    const validationCssClass = showValidation && !isValid(form, isEmailValid) ? 'was-validated' : '';
    const getInputClassName = (fieldName) => {
        const shouldShowValidation = showValidation || touchedFields[fieldName];
        return shouldShowValidation && !isFieldValid(fieldName, form, isEmailValid)
            ? 'form-control is-invalid'
            : 'form-control';
    };
    return (_jsxs("div", { className: `card shadow mb-3 ${validationCssClass}`.trim(), children: [_jsx("h2", { className: "card-header", children: "Account details" }), _jsxs("div", { className: "card-body", children: [subscriptionAlert == null ? null : (_jsx("div", { className: "mb-3", children: _jsxs("div", { className: subscriptionAlert.className, children: [_jsx("i", { className: subscriptionAlert.iconClass }), ' ', _jsx("span", { children: subscriptionAlert.text }), _jsx("br", {}), _jsxs("small", { children: ["To renew this service please contact us at", ' ', _jsx("a", { href: "mailto:sales@isfwatchkeeper.com", children: "sales@isfwatchkeeper.com" })] })] }) })), _jsxs("div", { className: "row mb-3", children: [_jsx("label", { className: "col-form-label col-12 col-lg-3", htmlFor: "accountDetailsUsername", children: "Username" }), _jsx("div", { className: "col-12 col-lg-9", children: _jsx("input", { id: "accountDetailsUsername", type: "text", className: "form-control-plaintext", value: form.username, readOnly: true }) })] }), _jsxs("div", { className: "row mb-3", children: [_jsx("label", { className: "col-form-label col-12 col-lg-3", htmlFor: "accountDetailsFullName", children: "Full name" }), _jsxs("div", { className: "col-12 col-lg-9", children: [_jsx("input", { id: "accountDetailsFullName", type: "text", className: getInputClassName('fullName'), value: form.fullName, onChange: (event) => handleChange('fullName', event.target.value), onBlur: () => handleBlur('fullName'), required: true }), _jsx("div", { className: "invalid-feedback", children: "Full name cannot be empty" })] })] }), _jsxs("div", { className: "row mb-3", children: [_jsx("label", { className: "col-form-label col-12 col-lg-3", htmlFor: "accountDetailsEmailAddress", children: "Email address" }), _jsxs("div", { className: "col-12 col-lg-9", children: [_jsx("input", { id: "accountDetailsEmailAddress", ref: emailInputRef, type: "email", pattern: emailAddressPattern, className: getInputClassName('emailAddress'), value: form.emailAddress, onChange: (event) => handleChange('emailAddress', event.target.value), onBlur: () => handleBlur('emailAddress'), required: true }), _jsx("div", { className: "invalid-feedback", children: "Please provide a valid email address" })] })] }), _jsxs("div", { className: "row mb-3", children: [_jsx("label", { className: "col-form-label col-12 col-lg-3", htmlFor: "accountDetailsCompanyName", children: "Company" }), _jsxs("div", { className: "col-12 col-lg-9", children: [_jsx("input", { id: "accountDetailsCompanyName", type: "text", className: getInputClassName('companyName'), value: form.companyName, onChange: (event) => handleChange('companyName', event.target.value), onBlur: () => handleBlur('companyName'), required: true }), _jsx("div", { className: "invalid-feedback", children: "Please provide a company name" })] })] }), _jsxs("div", { className: "row mb-3", children: [_jsx("label", { className: "col-form-label col-12 col-lg-3", htmlFor: "accountDetailsTelephoneNo", children: "Telephone" }), _jsxs("div", { className: "col-12 col-lg-9", children: [_jsx("input", { id: "accountDetailsTelephoneNo", type: "tel", className: getInputClassName('telephoneNo'), value: form.telephoneNo, onChange: (event) => handleChange('telephoneNo', event.target.value), onBlur: () => handleBlur('telephoneNo'), required: true }), _jsx("div", { className: "invalid-feedback", children: "Please provide a valid telephone number" })] })] }), _jsx("button", { type: "button", className: "btn btn-outline-primary", onClick: () => void saveAccountDetails(), disabled: isSaving, children: isSaving ? 'Please wait' : 'Save details' })] })] }));
};
